import React, { useState, useEffect, useRef } from 'react';
import { FaSearch, FaUser, FaSignOutAlt, FaBars } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const Header = ({ user, onLogout, onOpenSidebar, isSidebarCollapsed, onToggleSidebarCollapse }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);
  const [searchResults, setSearchResults] = useState([]);
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const navigate = useNavigate();

  const handleSearch = async (query) => {
    if (query.length < 2) {
      setSearchResults([]);
      setIsSearching(false);
      return [];
    }

    setIsSearching(true);
    try {
      const response = await fetch(`/api/search?q=${encodeURIComponent(query)}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setSearchResults(data);
      return data;
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
      return [];
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    handleSearch(query);
  };

  const handleSearchResultClick = (result) => {
    // Naviger til den relevante side baseret på resultat-typen
    let path = '/';
    if (result?.type === 'bil') {
      path = `/biler/profil/${result.id}`;
    } else if (result?.type === 'station') {
      path = `/stationer/profil/${result.id}`;
    } else if (result?.type === 'fejlmelding') {
      path = `/fejlmeldinger`;
    } else if (result?.route) {
      path = `/${result.route}`;
    }
    navigate(path);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleSearchKeyDown = async (e) => {
    if (e.key !== 'Enter' || e.nativeEvent?.isComposing) return;
    e.preventDefault();

    const trimmed = searchQuery.trim();
    if (!trimmed) {
      setSearchResults([]);
      return;
    }

    let results = searchResults;
    if ((!results || results.length === 0) && trimmed.length >= 2) {
      results = await handleSearch(trimmed);
    }

    if (results && results.length > 0) {
      handleSearchResultClick(results[0]);
    }
  };

  const handleClickOutside = () => {
    // Luk søgeresultater når der klikkes udenfor
    setTimeout(() => {
      setSearchResults([]);
    }, 200);
  };

  const handleLogout = () => {
    onLogout();
    setShowUserMenu(false);
  };

  // Luk brugermenu ved klik udenfor eller Escape
  useEffect(() => {
    const handleClickAway = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setShowUserMenu(false);
    };
    document.addEventListener('mousedown', handleClickAway);
    document.addEventListener('touchstart', handleClickAway, { passive: true });
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClickAway);
      document.removeEventListener('touchstart', handleClickAway);
      document.removeEventListener('keydown', handleKey);
    };
  }, []);

  // Sæt avatar fra user (DB) og lyt efter runtime-opdateringer
  useEffect(() => {
    setAvatarUrl(user?.avatar || null);
  }, [user]);

  useEffect(() => {
    const onAvatarUpdated = (e) => {
      const { avatar } = e?.detail || {};
      setAvatarUrl(avatar || null);
    };
    window.addEventListener('profile:avatarUpdated', onAvatarUpdated);
    return () => window.removeEventListener('profile:avatarUpdated', onAvatarUpdated);
  }, []);

  return (
    <header className="bg-white dark:bg-gray-950 shadow-sm border-b border-gray-200 dark:border-gray-800 px-4 py-3">
      <div className="flex items-center justify-between">
        {/* Venstre: mobil hamburger + titel */}
        <div className="flex items-center space-x-3">
          <button
            className="lg:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
            onClick={onOpenSidebar}
            aria-label="Åbn menu"
          >
            <FaBars />
          </button>
          <button
            className="hidden lg:inline-flex items-center justify-center p-2 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
            onClick={onToggleSidebarCollapse}
            aria-label="Fold sidebar"
            title={isSidebarCollapsed ? 'Udvid sidebar' : 'Kollaps sidebar'}
          >
            {isSidebarCollapsed ? '»' : '«'}
          </button>
        </div>

        {/* Søgefelt */}
        <div className="flex-1 max-w-lg mx-4 relative">
          <div className="relative">
            <input
              type="text"
              placeholder="Søg station, nummerplade, internt nr..."
              value={searchQuery}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              onBlur={handleClickOutside}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
            />
            {isSearching ? (
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
              </div>
            ) : (
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 h-4 w-4" />
            )}
          </div>

          {/* Søgeresultater */}
          {searchResults.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
              {searchResults.map((result, index) => (
                <div
                  key={`${result.type}-${result.id}-${index}`}
                  onClick={() => handleSearchResultClick(result)}
                  className="px-4 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900">{result.title}</div>
                      <div className="text-sm text-gray-500">{result.subtitle}</div>
                    </div>
                    <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">
                      {result.type}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Bruger menu */}
        <div className="relative" ref={userMenuRef}>
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            {avatarUrl ? (
              <img src={avatarUrl} alt="Avatar" className="w-8 h-8 rounded-full object-cover border border-gray-200 dark:border-gray-700" />
            ) : (
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                <FaUser className="text-white text-sm" />
              </div>
            )}
            <span className="hidden sm:block text-gray-700">{user?.username || 'Bruger'}</span>
          </button>

          {showUserMenu && (
            <div className="absolute right-0 mt-2 w-52 bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-800 z-50 overflow-hidden">
              <button
                onClick={() => { setShowUserMenu(false); navigate('/min-profil'); }}
                className="w-full flex items-center space-x-2 px-4 py-2 text-left text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                <FaUser className="text-gray-500" />
                <span>Min profil</span>
              </button>
              <button
                onClick={handleLogout}
                className="w-full flex items-center space-x-2 px-4 py-2 text-left text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                <FaSignOutAlt className="text-gray-500" />
                <span>Log af</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
